import Phaser from 'phaser';
import type { Enemy } from '../entities/Enemy';
import { audioManager } from '../audio/AudioManager';

const ATTACK_RANGE = 200; // px
const ATTACK_INTERVAL_BASE = 1000; // ms at attackSpeed = 1
const PROJECTILE_SPEED = 320; // px/s
const PROJECTILE_RADIUS = 5;
const PROJECTILE_COLOR = 0xffee00;

interface Projectile {
  circle: Phaser.GameObjects.Arc;
  target: Enemy;
  vx: number;
  vy: number;
}

/**
 * AutoAttack — fires a small yellow projectile toward the nearest enemy
 * within ATTACK_RANGE every (ATTACK_INTERVAL / attackSpeed) milliseconds.
 */
export class AutoAttack {
  private readonly scene: Phaser.Scene;
  private readonly getEnemies: () => Enemy[];
  private readonly getPlayerPos: () => { x: number; y: number };
  private readonly getDamage: () => number;
  private readonly getAttackSpeed: () => number;
  private readonly getCritChance: () => number;
  private readonly onKill: (enemy: Enemy) => void;

  private attackTimer = 0;
  private projectiles: Projectile[] = [];

  constructor(
    scene: Phaser.Scene,
    getPlayerPos: () => { x: number; y: number },
    getEnemies: () => Enemy[],
    onKill: (enemy: Enemy) => void,
    getDamage: () => number = () => 10,
    getAttackSpeed: () => number = () => 1,
    /** Returns the current crit chance as a fraction between 0 and 1. */
    getCritChance: () => number = () => 0,
  ) {
    this.scene = scene;
    this.getPlayerPos = getPlayerPos;
    this.getEnemies = getEnemies;
    this.onKill = onKill;
    this.getDamage = getDamage;
    this.getAttackSpeed = getAttackSpeed;
    this.getCritChance = getCritChance;
  }

  /** Call every frame from the scene's update(). */
  update(delta: number): void {
    const interval = ATTACK_INTERVAL_BASE / this.getAttackSpeed();
    this.attackTimer += delta;

    if (this.attackTimer >= interval) {
      this.attackTimer -= interval;
      this.tryFireAtNearest();
    }

    this.moveProjectiles(delta);
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private tryFireAtNearest(): void {
    const origin = this.getPlayerPos();
    const target = this.findNearestEnemy(origin);
    if (!target) return;

    const dx = target.x - origin.x;
    const dy = target.y - origin.y;
    const len = Math.sqrt(dx * dx + dy * dy);

    const circle = this.scene.add.arc(
      origin.x,
      origin.y,
      PROJECTILE_RADIUS,
      0,
      360,
      false,
      PROJECTILE_COLOR,
    );

    audioManager.play('shoot');

    this.projectiles.push({
      circle,
      target,
      vx: (dx / len) * PROJECTILE_SPEED,
      vy: (dy / len) * PROJECTILE_SPEED,
    });
  }

  private moveProjectiles(delta: number): void {
    const dt = delta / 1000;
    const remaining: Projectile[] = [];

    for (const proj of this.projectiles) {
      if (!proj.circle.active) continue;

      proj.circle.x += proj.vx * dt;
      proj.circle.y += proj.vy * dt;

      // Check hit
      if (proj.target.active && this.overlaps(proj.circle, proj.target)) {
        proj.circle.destroy();
        const isCrit = Math.random() < this.getCritChance();
        const damage = this.getDamage() * (isCrit ? 2 : 1);
        const died = proj.target.takeDamage(damage, isCrit);
        if (died) {
          audioManager.play('enemyDeath');
          this.onKill(proj.target);
          proj.target.die();
        } else {
          audioManager.play('enemyHit');
        }
        continue;
      }

      // Remove projectile if it has flown past the target
      if (this.isFarFromTarget(proj)) {
        proj.circle.destroy();
        continue;
      }

      remaining.push(proj);
    }

    this.projectiles = remaining;
  }

  private findNearestEnemy(origin: { x: number; y: number }): Enemy | null {
    let nearest: Enemy | null = null;
    let minDist = ATTACK_RANGE;

    for (const enemy of this.getEnemies()) {
      if (!enemy.active) continue;
      const dx = enemy.x - origin.x;
      const dy = enemy.y - origin.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= minDist) {
        minDist = dist;
        nearest = enemy;
      }
    }

    return nearest;
  }

  private overlaps(proj: Phaser.GameObjects.Arc, enemy: Enemy): boolean {
    const dx = proj.x - enemy.x;
    const dy = proj.y - enemy.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    return dist < PROJECTILE_RADIUS + 16;
  }

  /** Projectile has passed the target by more than 80px — consider it a miss. */
  private isFarFromTarget(proj: Projectile): boolean {
    const dx = proj.circle.x - proj.target.x;
    const dy = proj.circle.y - proj.target.y;
    return Math.sqrt(dx * dx + dy * dy) > 80;
  }

  /** Destroy all active projectiles. */
  destroy(): void {
    for (const proj of this.projectiles) {
      proj.circle.destroy();
    }
    this.projectiles = [];
  }
}
